import React from "react";
import { docsColors } from "@/lib/docs-theme";
import CodeBlock from "./CodeBlock";
import ParameterTable, { Parameter } from "./ParameterTable";

interface ErrorResponse {
  status: number;
  code: string;
  description: string;
}

interface ApiEndpointProps {
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  description: string;
  parameters?: Parameter[];
  queryParameters?: Parameter[];
  bodyParameters?: Parameter[];
  requestExample?: string;
  requestExampleLanguage?: "bash" | "json" | "javascript" | "python" | "ruby" | "php";
  responseExample?: string;
  errorResponses?: ErrorResponse[];
}

const methodColors = {
  GET: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  POST: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  PUT: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
  PATCH: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  DELETE: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

export default function ApiEndpoint({
  method,
  path,
  description,
  parameters,
  queryParameters,
  bodyParameters,
  requestExample,
  requestExampleLanguage = "bash",
  responseExample,
  errorResponses,
}: ApiEndpointProps) {
  return (
    <div className="my-8">
      <div className="flex items-center gap-3 mb-4">
        <span className={`px-3 py-1 rounded font-semibold text-sm ${methodColors[method]}`}>
          {method}
        </span>
        <code className={`text-lg font-mono ${docsColors.text.heading}`}>{path}</code>
      </div>

      <p className={`${docsColors.text.body} mb-6`}>{description}</p>

      {parameters && parameters.length > 0 && (
        <ParameterTable parameters={parameters} title="Path Parameters" />
      )}

      {queryParameters && queryParameters.length > 0 && (
        <ParameterTable parameters={queryParameters} title="Query Parameters" />
      )}

      {bodyParameters && bodyParameters.length > 0 && (
        <ParameterTable parameters={bodyParameters} title="Body Parameters" />
      )}

      {requestExample && (
        <div className="my-6">
          <h4 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>
            Example Request
          </h4>
          <CodeBlock language={requestExampleLanguage}>{requestExample.trim()}</CodeBlock>
        </div>
      )}

      {responseExample && (
        <div className="my-6">
          <h4 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>
            Example Response
          </h4>
          <CodeBlock language="json">{responseExample.trim()}</CodeBlock>
        </div>
      )}

      {errorResponses && errorResponses.length > 0 && (
        <div className="my-6">
          <h4 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>
            Error Responses
          </h4>
          <div className="space-y-3">
            {errorResponses.map((error) => (
              <div
                key={error.code}
                className={`p-4 rounded-lg ${docsColors.bg.card} border ${docsColors.border.default}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <span
                    className={`px-2 py-1 rounded text-sm font-medium ${docsColors.bg.code} ${docsColors.text.onDark}`}
                  >
                    {error.status}
                  </span>
                  <code className={`font-mono text-sm ${docsColors.text.heading}`}>
                    {error.code}
                  </code>
                </div>
                <p className={`text-sm ${docsColors.text.body}`}>{error.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
